import {html} from "lit";
import {Interval} from "luxon";
import {Subscription} from "rxjs";
import {container} from "tsyringe";
import {CommandName} from "../../commands/command-name.enum";
import {DialogOptions} from "../../components/ha-dialog/dialog-options.interface";
import {NOTIFICATION_DURATION} from "../../constants";
import {isIssueDone} from "./is-issue-done.helper";
import {UiHelper} from "../../helpers/ui.helper";
import {MessageBroker} from "../../message-broker/message-broker";
import {EntityChangedPayload} from "../../message-broker/payloads/entity-changed.payload";
import {EntityCreatedPayload} from "../../message-broker/payloads/entity-created.payload";
import {EntityDeletedPayload} from "../../message-broker/payloads/entity-deleted.payload";
import {CommandBus} from "../../services/command-bus.service";
import {IssuePropertyKeys} from "../../types/issue-property-keys.type";
import {ICalEvent} from "../dtos/i-cal-event.dto";
import {Employee} from "../entities/employee.entity";
import {IssueStatus} from "../entities/issue-status.entity";
import {IssueType} from "../entities/issue-type.entity";
import {Issue} from "../entities/issue.entity";
import {EmployeeRepository} from "../repositories/employee.repository";
import {IssueRepository} from "../repositories/issue.repository";
import {IssueStatusRepository} from "../repositories/issue-status.repository";
import {IssueTypeRepository} from "../repositories/issue-type.repository";
import {get} from "lit-translate";
import {PayloadOfDayAndRow} from "../../types/payload-of-day-and-row.type";

type UnixTimeStamp = number;
type TimelineRowUid = string;
type IssueUid = string;
type IssuesEditDialogOptions = Pick<DialogOptions<Issue>, `onClose`>

export class IssueHelper {

    static DAILY_DEFAULT: string = `8f58c30b-a018-4acf-8131-d60a4a0b982c`;

    private static _openedDialogHash: string | null = null;

    public static async assignChangedPropertyValues(issue: Issue, changedIssueValues: [IssuePropertyKeys, any][]): Promise<void> {
        for (const [key, value] of changedIssueValues) {
            switch (key) {
                case `type`:
                    const type = value instanceof IssueType ? value : await container.resolve(IssueTypeRepository).findOneById(value as string);
                    if (type) {
                        issue.type = type;
                    }
                    break;
                case `status`:
                    const status = value instanceof IssueStatus ? value : await container.resolve(IssueStatusRepository).findOneById(value as string);
                    if (status) {
                        issue.status = status;
                    }
                    break;
                case `creator`:
                    const creator = value instanceof Employee ? value : await container.resolve(EmployeeRepository).findOneById(value as string);
                    if (creator) {
                        issue.creator = creator;
                    }
                    break;
                default:
                    const o: Record<string, any> = {};
                    o[key] = value;

                    Object.assign(issue, o);
            }
        }
    }

    public static findInTimelineCellsByIssueTypeInInterval(
        timelineCells: Map<UnixTimeStamp, Map<TimelineRowUid, Map<IssueUid, PayloadOfDayAndRow>>>,
        interval: Interval
    ): Issue[] {
        const start = interval.start!.toUnixInteger();
        const end = interval.end!.toUnixInteger();

        return Array
            .from(timelineCells.entries())
            .filter(([unixTimeStampOfDay, _]) => unixTimeStampOfDay >= start && unixTimeStampOfDay <= end)
            .map(([, projectsMap]) => projectsMap)
            .flatMap(m => Array.from(m.values()))
            .flatMap(m => Array.from(m.values()))
            .map(payload => payload.issue);
    }

    /**
     * Whether this Issue's current status is "Done" (Anke, 2026-09-09: "es
     * gehört ja dann im Zustand done auch nicht mehr in die Ansicht hinein"
     * — My Organizer's boxes/calendar cells use this to both keep a DONE
     * Issue from ever being shown on a fresh load and to detect a
     * live-refresh transition into DONE, which they animate away instead of
     * just vanishing — see `<my-organizer-label-column>`/`<my-organizer-
     * calendar-day>`'s own `DissolveFlashController` usage).
     *
     * Delegates to `isIssueDone()` (`is-issue-done.helper.ts`, review-fund,
     * 2026-09-09, round 2) — the actual comparison lives there, in a module
     * with no heavy imports of its own, specifically so it can be tested
     * for real instead of only ever mocked.
     */
    public static isDone(issue: Issue): boolean {
        return isIssueDone(issue);
    }

    public static getTitle(iCalEvent: ICalEvent): string {
        const summary = iCalEvent.summary.trim();

        return summary
            .split(":")[0]
            .trim()
            .replaceAll(/\W{2,}/g, ' ')
            .trim();
    }

    /**
     * Opens the edit dialog for a single, existing issue. The dialog's "Neues Ticket"
     * button clones its template, so the template is a fresh, empty issue — the same
     * `IssueRepository.create()` the timeline uses for its cell template. Passing the
     * opened issue as template turned "Neues Ticket" into a second "Klonen" (Anke,
     * 2026-10-18). No template at all is no way out: the dialog then hides the button
     * (`when(!!this.template, …)` in ha-dialog-issue-edit-orchestrator.component.ts).
     * If building the template fails, the dialog still opens, only without that button.
     */
    public static async openIssueEditDialog(issue: Issue, options: IssuesEditDialogOptions = {}): Promise<void> {
        const template = await container.resolve(IssueRepository).create().catch((error: unknown) => {
            console.error(`[IssueHelper] Could not build the template for "Neues Ticket".`, error);
            return null;
        });
        IssueHelper.openIssuesEditDialog([issue], issue, template, options);
    }

    public static openIssuesEditDialog(
        issuesList: Issue[],
        selectedIssue: Issue | null = null,
        template: Issue | null = null,
        options: IssuesEditDialogOptions = {}
    ): void {
        const hash = [...issuesList]
            .sort((issueA, issueB) => issueA.uid.localeCompare(issueB.uid))
            .map(issue => issue.uid).sort().join('/');

        if (IssueHelper._openedDialogHash === hash) {
            return;
        }

        IssueHelper._openedDialogHash = hash;
        UiHelper.popover(null);

        const dialogOptions: DialogOptions = {
            ...{
                title: get('ha-boards-view.edit-issue.header.default'),
                maximizable: false,
                minimizable: false,
                closable: true,
                creationMode: false
            }, ...options
        };

        if (template) {
            dialogOptions.template = template;
        }

        const subscription: Subscription = UiHelper.openDialogEntitiesEdit(Issue, issuesList, dialogOptions, selectedIssue)
            .subscribe({
                next: async bulkCommand => {
                    if (!bulkCommand) {
                        return;
                    }

                    const commandBus = container.resolve(CommandBus);
                    if (await commandBus.dispatchCommand(bulkCommand)) {
                        const messageBroker = container.resolve(MessageBroker);
                        for (const command of bulkCommand.payload) {
                            const issue = command.payload;
                            const {uid, changeSet} = issue;
                            const entity = Issue;

                            switch (command.name) {
                                case CommandName.CREATE_ISSUE:
                                    messageBroker.publish<EntityCreatedPayload<Issue>>({
                                        type: `entity.created`,
                                        identityHash: issue.uid,
                                        payload: {
                                            uid: issue.uid,
                                            prototype: entity,
                                            entity: issue
                                        }
                                    });
                                    break;
                                case CommandName.DELETE_ISSUE:
                                    messageBroker.publish<EntityDeletedPayload<Issue>>({
                                        type: `entity.deleted`,
                                        identityHash: issue.uid,
                                        payload: {
                                            entity: issue
                                        }
                                    });
                                    break;
                                case CommandName.UPDATE_ISSUE:
                                    messageBroker.publish<EntityChangedPayload<Issue>>({
                                        type: `entity.changed`,
                                        identityHash: uid,
                                        payload: {
                                            uid,
                                            prototype: entity,
                                            changeSet
                                        }
                                    });
                                    break;
                            }
                        }
                    }
                },
                error: error => UiHelper.notify({
                    title: `Fehler im Dialog`,
                    content: html`<p>Meldung: «${error.message ?? `unbekannt`}»</p>`,
                    closable: true,
                    duration: NOTIFICATION_DURATION,
                    level: "error"
                }),
                complete: () => {
                    IssueHelper._openedDialogHash = null;
                    subscription.unsubscribe();
                    if (typeof dialogOptions.onClose === "function") {
                        dialogOptions.onClose(issuesList);
                    }
                }
            });
    }
}